import React, { ChangeEvent } from 'react';
import { Form } from '@ant-design/compatible';
import InputNumber from '../../../../../../components/input/InputNumber';
import Modal from '../../../../../../components/Modal';
import Anchor from '../../../../../../components/link/Anchor';
import { DATE_FORMAT } from '../../../../../../constants/service/common/utilConstants';
import InfoTable from '../../../../../../components/table/InfoTable';
import { ReviewFindOneResponseBodyReviewDto } from '../../types/ReviewDetailTypes';
import {
  PointLogsFindResponseBodyDto,
  PointLogsFindResponseBodyPointLogsDto,
} from '../../../../../../types/api/point-logs';
import { AccountUserDetailTypeV2 } from '../../../../../account/user/detail/common/types/accountUserDetail';
import {
  MAXIMUM_NUMBER_OF_POINT_MEMO_TEXT,
  POINT_LOG_TYPE_V2_TEXT,
} from '../../../../../../constants/service/app/pointConstants';
import { ACCOUNT_USER_DETAIL_URL_PATH } from '../../../../../../constants/service/common/urlConstants';
import { asyncSimpleModal, setDash, setDetailPageUrl, setNumberWithComma } from '../../../../../../lib/common';
import { sliceString } from '../../../../../../lib/utils/countCharacters';
import { setDateFormat } from '../../../../../../lib/moment';
import './ReviewPointRetrieveModal.scss';
import { FormComponentProps } from '@ant-design/compatible/lib/form';
import TextAreaForm from '../../../../../../components/form/TextAreaForm';
import CountOfTotal from '../../../../../../components/CountOfTotal';
import { MAXIMUM_NUMBER_OF_COUPON_MEMO_TEXT } from '../../../../../../constants/service/app/couponConstants';
import { formRequiredRules } from '../../../../../../lib/formRules';
import { CS_REVIEW_POINT_MEMO_LIMIT } from '../../../../../../constants/service/app/csReviewConstants';
import useFormValues from '../../hook/queries/useFormValues';

interface IReviewPointRetrieveModalProps extends FormComponentProps {
  isOpen: boolean;
  onClick: (status: string, memo: string) => void;
  closeModal: () => void;
  reviewDetailInfo: ReviewFindOneResponseBodyReviewDto;
  userDetailInfo: AccountUserDetailTypeV2 & { point: { credit: number; usedCredit: number } };
  pointLogsInfo: PointLogsFindResponseBodyDto;
}

const ReviewPointRetrieveModal = (props: IReviewPointRetrieveModalProps): JSX.Element => {
  const { isOpen, onClick, closeModal, reviewDetailInfo, userDetailInfo, pointLogsInfo, form } = props;
  const { status } = useFormValues();
  const { receivedPointAmount } = reviewDetailInfo;
  const {
    point: { credit, usedCredit },
  } = userDetailInfo;

  const memo = form.getFieldValue('memo');
  const isPossiblePointRetrieve = Number(credit) < Number(receivedPointAmount);

  const onChange = (value: string) => {
    if (value.length > CS_REVIEW_POINT_MEMO_LIMIT) {
      form.setFieldsValue({
        memo: sliceString(value, 0, CS_REVIEW_POINT_MEMO_LIMIT),
      });
    } else {
      form.setFieldsValue({
        memo: value,
      });
    }
  };

  return (
    <Modal
      title="포인트 회수"
      isOpen={isOpen}
      width={1200}
      height={600}
      isFooter={true}
      onOk={(e: ChangeEvent<HTMLButtonElement>) => {
        e.preventDefault();
        form.validateFields((errors, values) => {
          if (errors) {
            return;
          }
          onClick(status, values.memo);
          closeModal();
        });
      }}
      onClose={() => closeModal()}
    >
      <div className="cs-review-point-retrieve-modal">
        <div className="cs-review-point-retrieve-summary">
          <div>
            포인트 회수 가능 여부 :{' '}
            {receivedPointAmount > 0 ? <span className="valid">가능</span> : <span className="invalid">불가능</span>}
          </div>
          <div>
            <div className="label">사용 가능한 포인트 :</div>
            <InputNumber value={credit} disabled={true} />
            <span className="signs"> - </span>
            <InputNumber
              className={isPossiblePointRetrieve ? 'valid' : 'invalid'}
              value={receivedPointAmount}
              disabled={true}
            />
            <span className="signs"> = </span>
            <InputNumber
              className={isPossiblePointRetrieve ? 'valid' : 'invalid'}
              value={credit - receivedPointAmount}
              disabled={true}
            />
          </div>
          <div>
            <div className="label">사용한 포인트 :</div>
            <InputNumber value={usedCredit} disabled={true} />
            <span className="signs"> + </span>
            <InputNumber className="valid" value={Number(receivedPointAmount)} disabled={true} />
            <span className="signs"> = </span>
            <InputNumber className="valid" value={Number(usedCredit + receivedPointAmount)} disabled={true} />
          </div>
          <TextAreaForm
            form={form}
            label={
              <span>
                <span>메모</span>
                <CountOfTotal content={memo} maxCnt={MAXIMUM_NUMBER_OF_POINT_MEMO_TEXT} />
              </span>
            }
            initialValue={memo}
            maxLength={MAXIMUM_NUMBER_OF_COUPON_MEMO_TEXT}
            name="memo"
            rows={4}
            rules={[formRequiredRules(true, '메모를 입력해주세요.')]}
            onChange={onChange}
          />
          <span className="list-title">포인트 로그 리스트</span>
          {!pointLogsInfo && (
            <span className="point-retrieve-log-item-not-found">
              포인트 회수할 수 있는 포인트로그가 존재하지 않습니다
            </span>
          )}

          <div className="cs-review-point-retrieve-list">
            <InfoTable>
              <thead>
                <tr>
                  <th className="">번호</th>
                  <th>유저 ID</th>
                  <th>내역 종류</th>
                  <th>적립/사용처</th>
                  <th>발급한 관리자</th>
                  <th>메모</th>
                  <th>액수</th>
                  <th>생성 시간</th>
                </tr>
              </thead>
              <tbody>
                {pointLogsInfo &&
                  pointLogsInfo?.pointLogs.map((item: PointLogsFindResponseBodyPointLogsDto, index: number) => (
                    <tr key={item.id} className={item.pointIssueHistory ? 'point-retrieve-item' : ''}>
                      <td>{index + 1}</td>
                      <td>
                        <Anchor
                          color="primary"
                          isUnderLine
                          text={item.userId}
                          onClick={() => setDetailPageUrl(ACCOUNT_USER_DETAIL_URL_PATH, item.userId)}
                        />
                      </td>
                      <td>{setDash(POINT_LOG_TYPE_V2_TEXT[item.pointCause.type])}</td>
                      <td>{setDash(item?.pointCause.cause)}</td>
                      <td>{setDash(item?.pointIssueHistory?.admin.name)}</td>
                      <td>
                        {item.pointIssueHistory ? (
                          <Anchor
                            color="primary"
                            isUnderLine={true}
                            text={'내용 보기'}
                            onClick={async () => {
                              await asyncSimpleModal({
                                type: 'info',
                                title: '메모 내용',
                                message: item.pointIssueHistory ? item?.pointIssueHistory?.memo : '-',
                              });
                            }}
                          />
                        ) : (
                          '-'
                        )}
                      </td>
                      <td>{setNumberWithComma(item.amount)}</td>
                      <td>{setDateFormat(item.createDate, DATE_FORMAT.FULL_DASH_FORMAT)}</td>
                    </tr>
                  ))}
              </tbody>
            </InfoTable>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default Form.create()(ReviewPointRetrieveModal);
